import type { EmailType } from '../types'
import { escapeAttr } from './emailSignature.utils'
import type { TEMAS_REELS } from '../../lib/instagram-metricas'

/**
 * Bloque «Seguinos en Instagram» de los mails a clientes (plan-promo-instagram-mails, Fase 1).
 *
 * Funciones puras: deciden si el bloque va, lo renderizan y lo inyectan en el slot del EmailLayout.
 * La lectura de la configuración y de la cuenta vive en `netlify/functions/utils/promo-instagram.ts`.
 */

/**
 * Únicos tipos donde el bloque puede aparecer: los que recibe el cliente con tono neutro o positivo.
 * Los de deuda (`recordatorio_deuda`, `cobranza_aviso`, `recordatorio_lote_deudas`) y los internos
 * quedan afuera acá, en código: la configuración del estudio no puede habilitarlos.
 * La migración 20261020000000 repite esta lista en un CHECK.
 */
export const TIPOS_PROMO_PERMITIDOS = [
  'bienvenida_estudio',
  'contrato_cliente',
  'recibo_pago',
  'presentado_cliente',
  'presupuesto_enviado',
  'estado_cuenta',
] as const satisfies readonly EmailType[]

export type TipoPromo = (typeof TIPOS_PROMO_PERMITIDOS)[number]

export const esTipoPromo = (tipo: string): tipo is TipoPromo =>
  (TIPOS_PROMO_PERMITIDOS as readonly string[]).includes(tipo)

/**
 * Decide si un mail lleva el bloque. `estado_cuenta` solo va sin deuda: `saldo > 0` es deuda (el
 * template lo pinta en rojo) y un mail que reclama plata no promociona nada.
 */
export function debeMostrarPromo(p: {
  tipo: string
  activa: boolean
  tipos: readonly string[]
  data?: Record<string, unknown>
}): boolean {
  if (!p.activa || !esTipoPromo(p.tipo) || !p.tipos.includes(p.tipo)) return false
  if (p.tipo === 'estado_cuenta') {
    const saldo = p.data?.saldo
    return typeof saldo === 'number' && Number.isFinite(saldo) && saldo <= 0
  }
  return true
}

/** Usuario de Instagram: letras, números, punto y guion bajo, hasta 30. Acepta un `@` adelante. */
export function normalizarHandle(valor: string | null | undefined): string | null {
  const handle = valor?.trim().replace(/^@/, '') ?? ''
  return /^[A-Za-z0-9._]{1,30}$/.test(handle) ? handle : null
}

export const HOSTS_INSTAGRAM = ['instagram.com', 'www.instagram.com', 'ig.me'] as const

/**
 * Valida una URL antes de ponerla en un mail o de redirigir a ella. Exige https, compara el hostname
 * exacto (así `instagram.com.evil.com` o `user@host` no pasan) y rechaza `..` en el path, también
 * codificado. Con `prefijoPath`, el path tiene que empezar con él.
 */
export function esUrlPermitida(
  url: string,
  opts: { hosts: readonly string[]; prefijoPath?: string },
): boolean {
  let u: URL
  try {
    u = new URL(url)
  } catch {
    return false
  }
  // Caracteres de control (CR/LF, etc.): `new URL` los limpia, pero el string original los conserva y
  // podría terminar en un header Location.
  // eslint-disable-next-line no-control-regex -- a propósito: busca justamente caracteres de control
  if (/[\x00-\x1f\x7f]/.test(url)) return false
  if (u.protocol !== 'https:' || u.username || u.password || u.port) return false
  if (!opts.hosts.includes(u.hostname)) return false
  if (/(^|\/)\.\.(\/|$)|%2e/i.test(url.slice(url.indexOf(u.host) + u.host.length))) return false
  if (opts.prefijoPath && !u.pathname.startsWith(opts.prefijoPath)) return false
  return true
}

/** «+14 mil seguidores». `null` si no hay un número válido, y el bloque sale sin la cifra. */
export function formatoSeguidores(n: number | null | undefined): string | null {
  if (typeof n !== 'number' || !Number.isFinite(n) || n < 0) return null
  const entero = Math.floor(n)
  if (entero === 1) return '1 seguidor'
  if (entero < 1000) return `${entero} seguidores`
  if (entero < 1_000_000) return `+${Math.floor(entero / 1000)} mil seguidores`
  const millones = Math.floor(entero / 100_000) / 10
  return `+${millones.toLocaleString('es-AR')} M de seguidores`
}

type TemaReel = (typeof TEMAS_REELS)[number]

/** Quita acentos y pasa a minúsculas, para comparar texto escrito a mano. */
const normalizar = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

/**
 * Tema de reel que corresponde al impuesto de un mail de presentado. `iva` va como palabra completa:
 * como substring matchea «privada» o «activa». Sin match, `null` y el bloque no prefiere ningún tema.
 */
export function temaDeImpuesto(tipoImpuesto: string | null | undefined): TemaReel | null {
  if (!tipoImpuesto) return null
  const t = normalizar(tipoImpuesto)
  if (t.includes('monotributo')) return 'Monotributo'
  if (t.includes('ganancias')) return 'Ganancias'
  if (/ingresos brutos|\biibb\b|convenio multilateral/.test(t)) return 'Ingresos Brutos'
  if (/\b931\b|sueldo|cargas sociales|empleador/.test(t)) return 'Empleadores y sueldos'
  if (/sociedad|balance/.test(t)) return 'Sociedades'
  if (/\biva\b/.test(t)) return 'IVA'
  return null
}

const TITULO_MAX = 60

/**
 * Título de una publicación a partir del caption: la primera línea sin hashtags. Una línea larga se
 * queda con lo anterior a los dos puntos, o se corta en una palabra. Devuelve texto plano: el escape
 * se hace una sola vez, en el render.
 */
export function tituloDeCaption(caption: string | null | undefined): string {
  const linea = (caption ?? '').split('\n')[0].replace(/#[\p{L}\p{N}_]+/gu, '').replace(/\s+/g, ' ').trim()
  if (!linea) return ''
  if (linea.length <= TITULO_MAX) return linea
  const antesDosPuntos = linea.split(':')[0].trim()
  if (antesDosPuntos && antesDosPuntos.length <= TITULO_MAX) return antesDosPuntos
  const espacio = linea.lastIndexOf(' ', TITULO_MAX - 1)
  // Sin espacio, se corta por caracteres y no por unidades UTF-16, para no partir un emoji.
  const cortada = espacio > 0 ? linea.slice(0, espacio) : Array.from(linea).slice(0, TITULO_MAX - 1).join('')
  return `${cortada.trim()}…`
}

export interface PublicacionPromo {
  id: string
  permalink: string
  imagenUrl: string
  mediaType: string
  esReel: boolean
  caption: string | null
  /** ISO 8601. */
  publicadoAt: string
  tema: string | null
  /** Máximo de vistas capturado. `null` si todavía no hay métricas. */
  vistas: number | null
}

export const MAX_PUBLICACIONES_PROMO = 3
const DIA_MS = 86_400_000
const DIAS_PUBLICACION_PROMO = 90
const DIAS_MAS_VISTA = 30
/** Una norma vieja en el mail de un estudio contable puede inducir a error. */
const DIAS_NORMATIVA = 30
const TEMA_NORMATIVA: TemaReel = 'Novedades y normativa'

/**
 * Elige hasta 3 publicaciones para el mail, sin repetir y en este orden: la más reciente del tema del
 * mail (si hay tema), la más vista de los últimos 30 días, y después las más recientes.
 * Deja afuera lo de más de 90 días, lo que no tiene imagen y lo de normativa de más de 30 días.
 * Las ocultas y las que ya no están en Instagram las filtra la consulta.
 */
export function elegirPublicaciones(
  pubs: readonly PublicacionPromo[],
  opts: { tema: string | null; hoy: Date },
): PublicacionPromo[] {
  const ahora = opts.hoy.getTime()
  const edad = (p: PublicacionPromo) => ahora - Date.parse(p.publicadoAt)
  const vigentes = pubs
    .filter((p) => p.imagenUrl && Number.isFinite(edad(p)) && edad(p) >= 0 && edad(p) <= DIAS_PUBLICACION_PROMO * DIA_MS)
    .filter((p) => !(p.tema === TEMA_NORMATIVA && edad(p) > DIAS_NORMATIVA * DIA_MS))
    .sort((a, b) => edad(a) - edad(b))

  const elegidas: PublicacionPromo[] = []
  const sumar = (p: PublicacionPromo | undefined) => {
    if (p && elegidas.length < MAX_PUBLICACIONES_PROMO && !elegidas.some((e) => e.id === p.id)) elegidas.push(p)
  }
  if (opts.tema) sumar(vigentes.find((p) => p.tema === opts.tema))
  // La más vista del mes que no esté ya elegida: si la del tema es también la más vista, va la siguiente.
  sumar(
    vigentes
      .filter((p) => !elegidas.some((e) => e.id === p.id))
      .filter((p) => edad(p) <= DIAS_MAS_VISTA * DIA_MS && typeof p.vistas === 'number')
      .sort((a, b) => (b.vistas as number) - (a.vistas as number))[0],
  )
  for (const p of vigentes) sumar(p)
  return elegidas
}

export interface PromoInstagramProps {
  handle: string
  seguidores: number | null
  /**
   * URL del glifo oficial de Instagram en PNG (`GLIFO_INSTAGRAM_PATH`). Solo se usa si es https o
   * `http://localhost`. Sin URL válida, el bloque sale sin el glifo.
   */
  glifoUrl?: string
  /**
   * Transforma cada destino antes de ponerlo en el mail. Por defecto, el link directo. La Fase 3 pasa
   * acá el redirect firmado que cuenta los clics, sin tocar el render.
   */
  linkFn?: (destino: string) => string
  /**
   * Publicaciones ya elegidas (`elegirPublicaciones`) y con la imagen validada contra el bucket de la
   * organización. Sin publicaciones, el bloque sale igual que en la Fase 1: glifo, handle y botones.
   */
  publicaciones?: readonly PublicacionPromo[]
  /**
   * Mail del estudio para pedir que no le muestren el bloque (Ley 25.326, art. 27). No se usa «respondé
   * este mail»: los mails salen de no-reply y sin replyTo, así que esa respuesta no le llega a nadie.
   */
  contactoBaja?: string | null
}

const EMAIL_REGEX = /^[^\s@<>"']+@[^\s@<>"']+\.[^\s@<>"']+$/

/**
 * Línea final del bloque para pedir la baja. Con el mail del estudio, link `mailto:`; sin mail, un texto
 * que igual es cierto: el estudio lo apaga en la ficha del cliente.
 */
function pieBaja(contacto: string | null): string {
  const mail = contacto?.trim() ?? ''
  const destino = EMAIL_REGEX.test(mail)
    ? `escribinos a <a href="mailto:${escapeAttr(mail)}" style="color:${COLOR_TEXTO_SUAVE};text-decoration:underline;">${escapeAttr(mail)}</a>`
    : 'avisanos'
  return (
    `<p style="margin:16px 0 0 0;font-size:12px;line-height:16px;color:${COLOR_TEXTO_SUAVE};">` +
    `¿No querés ver esto en tus mails? Si preferís que lo saquemos, ${destino}.</p>`
  )
}

/** Path del glifo oficial de Instagram dentro de `public/`. */
export const GLIFO_INSTAGRAM_PATH = '/email/instagram-glifo.png'

const COLOR_TEXTO = '#262626'
const COLOR_TEXTO_SUAVE = '#6b6b6b'
/**
 * Fondo sólido, sin degradado. La app de Gmail en modo oscuro invierte los colores del mail pero no las
 * imágenes de fondo: con el degradado como `background-image`, la tarjeta quedaba clara y el texto,
 * invertido a claro, no se leía. Con un color sólido Gmail invierte tarjeta y texto juntos.
 */
const FONDO_TARJETA = '#fbf3f8'
/**
 * Magenta de Instagram con texto blanco (contraste 5,1:1). Un color saturado Gmail casi no lo invierte,
 * así el botón principal sigue destacando en modo oscuro. El oscuro anterior se invertía a gris claro.
 */
const COLOR_BOTON_PRINCIPAL = '#c13584'
const BORDE_BOTON_SECUNDARIO = '#e3d3ea'
const FUENTE = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Ubuntu, sans-serif"

/**
 * Botón que Outlook respeta: el padding y el fondo van en el `<td>`, porque Outlook ignora el padding
 * de un `<a>`. El principal es magenta con texto blanco. El secundario es blanco con borde.
 */
function boton(href: string, texto: string, relleno: boolean): string {
  const fondo = relleno ? COLOR_BOTON_PRINCIPAL : '#ffffff'
  const borde = relleno ? 'padding:11px 22px;' : `padding:10px 21px;border:1px solid ${BORDE_BOTON_SECUNDARIO};`
  return (
    `<table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>` +
    `<td align="center" bgcolor="${fondo}" style="border-radius:8px;${borde}background-color:${fondo};">` +
    `<a href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer" ` +
    `style="font-family:${FUENTE};font-size:15px;font-weight:600;line-height:18px;color:${relleno ? '#ffffff' : COLOR_TEXTO};text-decoration:none;">` +
    `${escapeAttr(texto)}</a></td></tr></table>`
  )
}

/** `<img>` del glifo de Instagram. Devuelve '' si la URL no es https ni `http://localhost`. */
function glifoHtml(url: string | undefined): string {
  if (!url || !/^(https:\/\/|http:\/\/localhost[:/])/i.test(url)) return ''
  return (
    `<img src="${escapeAttr(url)}" width="52" height="52" alt="Instagram" ` +
    `style="display:block;border:0;outline:none;width:52px;height:52px;margin:0 auto 26px auto;">`
  )
}

/**
 * Ancho de cada tarjeta. Tres tarjetas con su margen (3 × (144 + 12) = 468) entran en el área útil del
 * bloque: 600 del contenedor − 80 de padding − 40 de la celda − 2 de borde = 478. Con más ancho, Gmail
 * pasa la tercera a otra fila y Outlook la saca del área útil.
 */
export const ANCHO_TARJETA = 144

const etiquetaPublicacion = (p: PublicacionPromo) =>
  p.esReel || p.mediaType === 'VIDEO' ? '▶ Reel' : p.mediaType === 'CAROUSEL_ALBUM' ? 'Carrusel' : 'Post'

/**
 * Una tarjeta de la grilla: portada, etiqueta y título, todo como link a la publicación. La imagen
 * lleva `width` como atributo (Outlook ignora el CSS) y alto automático, así no se deforma.
 * Devuelve '' si el permalink no es de Instagram, la imagen no es https o `linkFn` no da https.
 */
function tarjetaPublicacion(p: PublicacionPromo, linkFn: (destino: string) => string): string {
  if (!esUrlPermitida(p.permalink, { hosts: HOSTS_INSTAGRAM })) return ''
  if (!/^https:\/\//i.test(p.imagenUrl)) return ''
  const href = linkFn(p.permalink)
  if (!/^https:\/\//i.test(href)) return ''
  const titulo = tituloDeCaption(p.caption)
  const etiqueta = etiquetaPublicacion(p)
  return (
    `<div style="display:inline-block;width:${ANCHO_TARJETA}px;max-width:100%;margin:6px;vertical-align:top;text-align:left;">` +
    `<a href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;color:${COLOR_TEXTO};">` +
    `<img src="${escapeAttr(p.imagenUrl)}" width="${ANCHO_TARJETA}" alt="${escapeAttr(titulo || etiqueta)}" ` +
    `style="display:block;border:0;outline:none;width:${ANCHO_TARJETA}px;max-width:100%;height:auto;border-radius:10px;">` +
    `<span style="display:block;margin:8px 0 2px 0;font-family:${FUENTE};font-size:11px;line-height:14px;font-weight:600;color:${COLOR_TEXTO_SUAVE};text-transform:uppercase;letter-spacing:0.4px;">${escapeAttr(etiqueta)}</span>` +
    (titulo
      ? `<span style="display:block;font-family:${FUENTE};font-size:13px;line-height:18px;color:${COLOR_TEXTO};">${escapeAttr(titulo)}</span>`
      : '') +
    `</a></div>`
  )
}

/**
 * Grilla de hasta 3 publicaciones. Fluid hybrid: tarjetas `inline-block` que se apilan solas en pantallas
 * angostas, y una tabla condicional para Outlook Windows, que no entiende `inline-block`.
 */
function grillaPublicaciones(pubs: readonly PublicacionPromo[], linkFn: (destino: string) => string): string {
  const tarjetas = pubs
    .slice(0, MAX_PUBLICACIONES_PROMO)
    .map((p) => tarjetaPublicacion(p, linkFn))
    .filter(Boolean)
  if (!tarjetas.length) return ''
  const celdaMso = `<!--[if mso]></td><td valign="top" width="${ANCHO_TARJETA + 12}" style="padding:0;"><![endif]-->`
  return (
    `<p style="margin:0 0 6px 0;font-size:12px;line-height:16px;font-weight:600;color:${COLOR_TEXTO_SUAVE};text-transform:uppercase;letter-spacing:0.6px;">Lo último</p>` +
    `<div style="font-size:0;line-height:0;margin:0 0 14px 0;">` +
    `<!--[if mso]><table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center"><tr><td valign="top" width="${ANCHO_TARJETA + 12}" style="padding:0;"><![endif]-->` +
    tarjetas.join(celdaMso) +
    `<!--[if mso]></td></tr></table><![endif]-->` +
    `</div>`
  )
}

/**
 * Bloque «Seguinos en Instagram» con el glifo y los botones «Seguir en Instagram» y «Enviar mensaje».
 *
 * La tarjeta lleva un fondo rosado sólido (ver `FONDO_TARJETA`: un degradado se rompe en el modo oscuro
 * de Gmail). Los botones son `inline-block`: en pantallas angostas se apilan solos, sin media
 * queries (el EmailLayout no tiene `<style>` en el head y Gmail descarta el que va en el body). Outlook
 * Windows no entiende `inline-block`, así que ahí los alinea una tabla condicional `<!--[if mso]>`.
 *
 * Devuelve '' si el handle no es válido o si `linkFn` devuelve algo que no es https.
 */
export function renderPromoInstagramHtml({
  handle,
  seguidores,
  glifoUrl,
  linkFn = (d) => d,
  publicaciones = [],
  contactoBaja = null,
}: PromoInstagramProps): string {
  const usuario = normalizarHandle(handle)
  if (!usuario) return ''

  const perfil = `https://www.instagram.com/${usuario}/`
  const mensaje = `https://ig.me/m/${usuario}`
  if (!esUrlPermitida(perfil, { hosts: HOSTS_INSTAGRAM }) || !esUrlPermitida(mensaje, { hosts: HOSTS_INSTAGRAM })) {
    return ''
  }
  const hrefPerfil = linkFn(perfil)
  const hrefMensaje = linkFn(mensaje)
  if (![hrefPerfil, hrefMensaje].every((href) => /^https:\/\//i.test(href))) return ''

  const cifra = formatoSeguidores(seguidores)
  const linea =
    `<a href="${escapeAttr(hrefPerfil)}" target="_blank" rel="noopener noreferrer" ` +
    `style="color:${COLOR_TEXTO};font-weight:600;text-decoration:none;">@${escapeAttr(usuario)}</a>` +
    (cifra ? ` · ${escapeAttr(cifra)}` : '')

  const envoltorioBoton = 'display:inline-block;margin:4px;vertical-align:top;'

  return (
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" data-promo-instagram="true">` +
    `<tr><td style="padding:28px 40px 0 40px;">` +
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="${FONDO_TARJETA}" ` +
    `style="border-radius:16px;background-color:${FONDO_TARJETA};">` +
    `<tr><td align="center" style="padding:28px 20px 24px 20px;font-family:${FUENTE};">` +
    glifoHtml(glifoUrl) +
    `<p style="margin:0;font-size:18px;line-height:24px;font-weight:700;color:${COLOR_TEXTO};">Seguinos en Instagram</p>` +
    `<p style="margin:6px 0 18px 0;font-size:14px;line-height:20px;color:${COLOR_TEXTO_SUAVE};">${linea}</p>` +
    grillaPublicaciones(publicaciones, linkFn) +
    `<div style="font-size:0;line-height:0;">` +
    `<!--[if mso]><table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center"><tr><td style="padding:0 4px;"><![endif]-->` +
    `<div style="${envoltorioBoton}">${boton(hrefPerfil, 'Seguir en Instagram', true)}</div>` +
    `<!--[if mso]></td><td style="padding:0 4px;"><![endif]-->` +
    `<div style="${envoltorioBoton}">${boton(hrefMensaje, 'Enviar mensaje', false)}</div>` +
    `<!--[if mso]></td></tr></table><![endif]-->` +
    `</div>` +
    pieBaja(contactoBaja) +
    `</td></tr></table>` +
    `</td></tr></table>`
  )
}

/**
 * Regex del slot del bloque. Igual que el de la firma (ORR-112): react-email renderiza el
 * `<Section data-promo-slot>` como `<table>`, no como `<section>`. El slot no anida tablas.
 */
export const PROMO_SLOT_REGEX = /<table[^>]*\bdata-promo-slot="true"[^>]*>[\s\S]*?<\/table>/i

/** Reemplaza el slot por el bloque. Con `promoHtml` vacío, el slot desaparece sin dejar marcas. */
export function injectPromoIntoSlot(html: string, promoHtml: string): string {
  // Replacer como función: un `$&` dentro del bloque no se interpreta como patrón de reemplazo.
  return html.replace(PROMO_SLOT_REGEX, () => promoHtml)
}
